import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnSort,
  SortingState,
  OnChangeFn,
  PaginationState,
  getPaginationRowModel,
  ColumnResizeMode,
  ColumnSizingState,
} from '@tanstack/react-table';
import { GTSort, GTRow, GTSearch, GTRowEmpty, GTResizerIcon } from './comps';
import { textFilter, getClassNameByHeaderId } from './util';
import { GTInternalIds } from './types';
import { Poppins, Spacer } from 'src/common';
import { PagNavButton, PagNumberButton } from 'src/common/pagination/PagNavButton';
import { GenericSelect } from '../select/GenericSelect';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { getPageRange } from 'src/utils/misc';
import { captureException } from '@sentry/react';
import { GTProvider } from './GTContext';

interface DivProps {
  $roundedCorners: boolean;
  $maxHeight?: number | string | null;
  $stickyHeader?: boolean;
  $darkMode?: boolean;
  $columnResizing?: boolean;
}

const Div = styled.div<DivProps>`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  overflow-y: auto;
  position: relative;
  max-height: ${({ $maxHeight }) =>
    typeof $maxHeight === 'number' ? `${$maxHeight}px` : $maxHeight ? `${$maxHeight}` : 'auto'};

  ${({ $roundedCorners }) =>
    $roundedCorners &&
    css`
      border-top-left-radius: 6px !important;
      border-top-right-radius: 6px !important;
    `};

  ${({ $stickyHeader }) =>
    $stickyHeader &&
    css`
      thead {
        position: sticky;
        top: 0;
        z-index: 1;
        width: 100%;
        z-index: 9;
        box-shadow: 0px 0px 30px ${({ theme }) => theme.colors.shadowLight};
      }
    `};

  ${({ $darkMode }) =>
    $darkMode &&
    css`
      background-color: ${({ theme }) => theme.colors.cultured};
      table {
        thead {
          background-color: ${({ theme }) => theme.colors.cultured} !important;
        }
      }
    `};

  table th:first-child {
  }

  table {
    width: 100%;
    border-collapse: collapse;
    ${({ $columnResizing }) => $columnResizing && 'table-layout: fixed;'}

    th,
    td {
      padding: 0 5px;
      color: ${({ theme }) => theme.colors.prussianBlue};
    }

    tbody td.gt-data-cell {
      padding: 0;
    }

    tbody td.gt-data-cell:not(:has(.gt-inline-wrapper)) > div {
      padding: 0 5px;
    }

    tbody td.gt-data-cell:hover {
      background-color: ${({ theme }) => theme.colors.cultured};
    }

    thead {
      background-color: ${({ theme }) => theme.colors.white};
      .gt-checkbox {
        margin-top: 6px;
      }
      tr {
        th {
          user-select: none;
          text-align: left;
        }
      }
    }

    .gt-left-spacer {
      width: 10px !important;
      table-layout: fixed;
    }
    .gt-align-right {
      text-align: right;
    }
    .gt-align-left {
      text-align: left;
    }

    .gt-width-10 {
      width: 10px;
      width: 10px !important;
      table-layout: fixed;
    }

    th:last-child,
    td:has(.ThreeDots__Wraper) {
      position: sticky;
      right: 0;
      background: ${({ theme, $darkMode }) => ($darkMode ? theme.colors.cultured : theme.colors.white)};
      z-index: 2;
      padding-bottom: 1px;
    }

    thead th:last-child {
      z-index: 4;
    }

    .resizer {
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      width: 12px;
      cursor: col-resize;
      user-select: none;
      touch-action: none;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;

      .resizer-icon {
        height: 60%;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.3;
        transition: opacity 0.2s;
      }

      &:hover,
      &.isResizing {
        .resizer-icon {
          opacity: 1;
        }
      }
    }

    th {
      position: relative;
      overflow: visible;
    }
  }
`;

const TH = styled.th<{ $itemHeight: number }>`
  height: ${({ $itemHeight }) => $itemHeight * 0.8}px !important;
  position: relative;
  overflow: hidden !important;
  max-width: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: ${({ $itemHeight }) => $itemHeight * 0.7}px !important;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 35px !important;
  }
`;

const HeaderContent = styled.div<{ $cursorPointer: boolean; $canOverflow: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  font-weight: 600;
  cursor: ${({ $cursorPointer }) => ($cursorPointer ? 'pointer' : 'default')};
  max-width: 100%;
  padding-right: 15px;

  .header-text {
    overflow: ${({ $canOverflow }) => ($canOverflow ? 'visible' : 'hidden')};
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .header-sort {
    flex-shrink: 0;
    margin-left: 6px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 16px !important;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 12px !important;
  }
`;

const AnchorWrap = styled.div<{ $itemHeight: number }>`
  position: relative;
  height: 0px;
  z-index: 10;

  #column-select-anchor {
    position: absolute;
    right: 20px;
    top: ${({ $itemHeight }) => ($itemHeight * 0.8) / 2}px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ theme }) => theme.breakpoints.down('md')} {
      top: ${({ $itemHeight }) => ($itemHeight * 0.7) / 2}px;
    }
    ${({ theme }) => theme.breakpoints.down('sm')} {
      top: 17px;
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;

  .pag-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    &__right {
      display: flex;
      gap: 10px;
      &__main-btn {
        width: 150px;
      }
    }
  }

  .pag-stats {
    margin-top: 8px;
    display: flex;
    justify-content: flex-end;
    &__end {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .pag-controls {
      &__right {
        display: flex;
        gap: 8px;
        &__main-btn {
          width: unset;
        }
      }
    }
    .pag-stats {
      justify-content: space-between;
    }
  }
`;

interface GenericTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  expandContent?: (info: T) => React.ReactNode;
  itemHeight?: 50 | 77;
  searchable?: true | string[];
  emptyRow?: JSX.Element;
  initialSort?: SortingState;
  GTColumnSelectAnchorExported: JSX.Element;
  rowDisabled?: (row: T) => boolean;
  onSortChange?: (sort: SortingState) => void;
  onBottomReached?: () => void;
  layout?: {
    maxHeight?: number | string | null;
    stickyHeader?: boolean;
  };
  darkMode?: boolean;
  controledSorting?: { state: ColumnSort; onChange: (a: ColumnSort) => void };
  usePagination?: { pageSize?: number; onPaginationChange?: (e: PaginationState) => void } | null;
  tableId: TableIds;
  enableColumnResize?: boolean;
  onColumnSizingChange?: (columnSizing: ColumnSizingState) => void;
  defaultColumnSizing?: ColumnSizingState;
  hideExpandContentWhenClosed?: boolean;
  getRowCanExpand?: (row: T) => boolean;
  externalSearchValue?: string;
  onSearchChange?: (value: string) => void;
  searchInputRef?: React.RefObject<HTMLInputElement>;
}

const GenericTable = <T,>({
  data,
  columns,
  expandContent,
  itemHeight = 77,
  searchable,
  emptyRow,
  initialSort,
  GTColumnSelectAnchorExported,
  rowDisabled,
  onSortChange,
  onBottomReached,
  layout,
  darkMode,
  controledSorting,
  usePagination = { pageSize: 50 },
  tableId,
  enableColumnResize = true,
  onColumnSizingChange,
  defaultColumnSizing,
  hideExpandContentWhenClosed = false,
  getRowCanExpand,
  externalSearchValue,
  onSearchChange,
  searchInputRef,
}: GenericTableProps<T>) => {
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');
  const globalFilter = externalSearchValue !== undefined ? externalSearchValue : internalGlobalFilter;

  const handleGlobalFilterChange = (value: string) => {
    if (externalSearchValue !== undefined && onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalGlobalFilter(value);
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const [sorting, setSorting] = useLocalStorage<SortingState>(`${tableId}-sort`, initialSort || []);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: usePagination?.pageSize || 50,
  });

  const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
    `${tableId}-column-sizing`,
    defaultColumnSizing || {},
  );
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const isMd = useBreakpoints('md');
  const isSm = useBreakpoints('sm');
  const adjustedItemHeight = isSm ? 50 : itemHeight;

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (data.length && scrollHeight - scrollTop - clientHeight < 300) {
        onBottomReached && onBottomReached();
      }
    }
  };

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, []);

  const scrollToTable = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.style.scrollMarginTop = '160px';
      tableContainerRef.current.scrollIntoView();
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.style.scrollMarginTop = '';
        }
      }, 1000);
    }
  };

  const ignoredColumns: string[] = [];

  const table = useReactTable({
    data,
    columns: ignoredColumns.length ? columns.filter((col) => !ignoredColumns.includes(col.id!)) : columns,
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: getRowCanExpand
      ? (row) => getRowCanExpand(row.original)
      : (row) => !!expandContent && !!expandContent(row.original),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: !!usePagination ? getPaginationRowModel() : undefined,
    onPaginationChange: setPagination,
    enableGlobalFilter: !!searchable,
    filterFns: {
      textSearch: (row, columnId, value, addMeta) => textFilter(row, columnId, value, addMeta, searchable),
    },
    onGlobalFilterChange: handleGlobalFilterChange,
    globalFilterFn: 'textSearch',
    autoResetPageIndex: false,
    state: {
      globalFilter,
      sorting,
      pagination,
      columnSizing,
    },
    initialState: {
      sorting: initialSort,
      columnSizing: columnSizing,
    },
    onSortingChange: (a) => {},
    enableColumnResizing: enableColumnResize,
    columnResizeMode,
    onColumnSizingChange: (updater) => {
      const newColumnSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newColumnSizing);
      onColumnSizingChange?.(newColumnSizing);
    },
    defaultColumn: enableColumnResize
      ? {
          minSize: 60,
          size: 160,
          maxSize: 5000,
        }
      : undefined,
  });

  useEffect(() => {
    const handleColumnSizingReset = (event: CustomEvent<{ tableId: string }>) => {
      if (event.detail.tableId === tableId) {
        setColumnSizing({});
        table.setColumnSizing({});
      }
    };

    window.addEventListener('column-sizing-reset', handleColumnSizingReset as EventListener);

    return () => {
      window.removeEventListener('column-sizing-reset', handleColumnSizingReset as EventListener);
    };
  }, [tableId, setColumnSizing, table]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    // @ts-ignore
    setSorting(updater);
  };

  useEffect(() => {
    if (!!onSortChange) {
      onSortChange(sorting);
    }
  }, [sorting, onSortChange]);

  useEffect(() => {
    if (!!usePagination?.onPaginationChange) {
      usePagination.onPaginationChange(pagination);
    }
  }, [usePagination]);

  table.setOptions((prev) => ({
    ...prev,
    onSortingChange: (a) => {
      try {
        // @ts-ignore
        mpEvent(MPEvents.TableSortChange, a()[0]);
      } catch (error) {
        captureException(error);
      }

      handleSortingChange(a);
    },
  }));

  const { start, end } = getPageRange(
    table.getState().pagination.pageIndex,
    table.getPageCount(),
    isSm ? 3 : isMd ? 5 : 7,
  );

  const selectComponent = () => {
    return (
      <GenericSelect
        value={pagination.pageSize}
        onChange={(opt) => {
          const value = opt.value as number;
          const newPageIndex = Math.floor((pagination.pageIndex * pagination.pageSize) / value);
          setPagination({ pageIndex: newPageIndex, pageSize: value });
        }}
        white
        width={120}
        placeholder="Select page size"
        options={[
          {
            label: '20',
            value: 20,
          },
          {
            label: '50',
            value: 50,
          },
          {
            label: '100',
            value: 100,
          },
        ]}
      />
    );
  };

  return (
    <GTProvider value={{ itemHeight: adjustedItemHeight, portalTarget: null }}>
      {!!searchable && (
        <GTSearch
          value={globalFilter}
          onChange={handleGlobalFilterChange}
          tableId={tableId}
          inputRef={searchInputRef}
        />
      )}
      {GTColumnSelectAnchorExported && (
        <AnchorWrap $itemHeight={adjustedItemHeight}>
          <div id="column-select-anchor" data-cy={`gt-sca-${tableId}`}>
            {React.cloneElement(GTColumnSelectAnchorExported, { tableId })}
          </div>
        </AnchorWrap>
      )}

      <Div
        $roundedCorners={!searchable}
        className="styled-scroll"
        data-cy={`gt-${tableId}`}
        ref={tableContainerRef}
        onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
        $maxHeight={layout?.maxHeight}
        $stickyHeader={layout?.stickyHeader}
        $darkMode={darkMode}
        $columnResizing={enableColumnResize}
        style={enableColumnResize ? { overflowX: 'auto', overflowY: 'auto' } : undefined}
      >
        <table
          style={
            enableColumnResize
              ? {
                  width: table.getCenterTotalSize(),
                  minWidth: '100%',
                }
              : undefined
          }
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => {
              return (
                <tr key={headerGroup.id} data-cy="gt-tr">
                  <th className="gt-left-spacer" />
                  {headerGroup.headers.map((header) => {
                    const { id, column, isPlaceholder } = header;
                    const canSort = column.getCanSort();
                    const isSorted = column.getIsSorted();
                    const isAsc = isSorted === 'asc';
                    const isInternalCol =
                      id === GTInternalIds.checker || id === GTInternalIds.expander || id === GTInternalIds.tdm;

                    const sortHandler = controledSorting
                      ? () => {
                          controledSorting.onChange({
                            id: id,
                            desc: !controledSorting.state.desc,
                          });
                        }
                      : column.getToggleSortingHandler();

                    let headerTitle = '';
                    if (typeof column.columnDef.header === 'string') {
                      headerTitle = column.columnDef.header;
                    }

                    return (
                      <TH
                        key={id}
                        style={{
                          width: header.getSize(),
                          minWidth: isInternalCol ? 40 : undefined,
                          maxWidth: column.columnDef.maxSize,
                          tableLayout: isInternalCol ? 'fixed' : undefined,
                        }}
                        className={getClassNameByHeaderId(id)}
                        $itemHeight={adjustedItemHeight}
                      >
                        <HeaderContent
                          $cursorPointer={canSort}
                          onClick={canSort ? sortHandler : undefined}
                          title={headerTitle}
                          $canOverflow={isInternalCol}
                        >
                          <div className="header-text">
                            {!isPlaceholder && flexRender(column.columnDef.header, header.getContext())}
                          </div>
                          <div className="header-sort">
                            {controledSorting && controledSorting.state.id === id && (
                              <GTSort isSorted isAsc={!controledSorting.state.desc} />
                            )}
                            {!controledSorting && canSort && <GTSort isSorted={!!isSorted} isAsc={isAsc} />}
                          </div>
                        </HeaderContent>
                        {enableColumnResize && !isInternalCol && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                          >
                            <div className="resizer-icon">
                              <GTResizerIcon />
                            </div>
                          </div>
                        )}
                      </TH>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {table.getRowCount() > 0
              ? table.getRowModel().rows.map((row) => {
                  let content: null | React.ReactNode = null;
                  if (expandContent) {
                    content = expandContent(row.original);
                  }
                  return (
                    <GTRow<T>
                      row={row}
                      content={content}
                      itemHeight={adjustedItemHeight}
                      disabled={rowDisabled ? rowDisabled(row.original) : undefined}
                      tableId={tableId}
                      hideExpandContentWhenClosed={hideExpandContentWhenClosed}
                    />
                  );
                })
              : emptyRow || (
                  <GTRowEmpty text={globalFilter ? 'No results found' : undefined} itemHeight={adjustedItemHeight} />
                )}
          </tbody>
        </table>
      </Div>

      {usePagination && (table.getPageCount() > 1 || data.length > 20) && (
        <>
          <Spacer $px={20} />
          <PaginationContainer data-cy="gt-pagination">
            <div className="pag-controls">
              {!isMd && selectComponent()}
              <div className="pag-controls__right">
                <PagNavButton
                  onClick={() => {
                    table.firstPage();
                    scrollToTable();
                  }}
                  disabled={!table.getCanPreviousPage()}
                >
                  {'<<'}
                </PagNavButton>
                <PagNavButton
                  className="pag-controls__right__main-btn"
                  onClick={() => {
                    table.previousPage();
                    scrollToTable();
                  }}
                  disabled={!table.getCanPreviousPage()}
                >
                  {'<'}
                  {!isMd && 'Previous'}
                </PagNavButton>

                {Array.from({ length: end - start + 1 }).map((_, i) => {
                  const pageIndex = start + i;
                  return (
                    <PagNumberButton
                      key={pageIndex}
                      selected={pageIndex === table.getState().pagination.pageIndex}
                      onClick={() => {
                        table.setPageIndex(pageIndex);
                        scrollToTable();
                      }}
                    >
                      {pageIndex + 1}
                    </PagNumberButton>
                  );
                })}
                <PagNavButton
                  className="pag-controls__right__main-btn"
                  onClick={() => {
                    table.nextPage();
                    scrollToTable();
                  }}
                  disabled={!table.getCanNextPage()}
                >
                  {!isMd && 'Next'} {'>'}
                </PagNavButton>
                <PagNavButton
                  onClick={() => {
                    table.lastPage();
                    scrollToTable();
                  }}
                  disabled={!table.getCanNextPage()}
                >
                  {'>>'}
                </PagNavButton>
              </div>
            </div>
            <Spacer $px={4} />
            <div className="pag-stats">
              {isMd && selectComponent()}
              <div className="pag-stats__end">
                <Poppins px={14} color="cflowerBlue">
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
                </Poppins>
                <Poppins px={14} color="cflowerBlue">
                  Showing {table.getRowModel().rows.length.toLocaleString()} of {table.getRowCount().toLocaleString()}{' '}
                  Rows
                </Poppins>
              </div>
            </div>
          </PaginationContainer>
        </>
      )}
    </GTProvider>
  );
};

export default GenericTable;
